// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsCheckoutPage } from "@plumberssupply/App/Store/Selectors/GeneralSelectors";
import { xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: InjectableCss;
    linksWrapper?: InjectableCss;
    linkList1GridItem?: InjectableCss;
    linkList2GridItem?: InjectableCss;
    linkList3GridItem?: InjectableCss;
    linkList4GridItem?: InjectableCss;
    contactUsGridItem?: InjectableCss;
    richContentWrapper?: InjectableCss;
    checkoutRichContentWrapper?: InjectableCss;
    privacyPloicyContainer?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding-bottom: 20px;
            ${xScreen("lte", "md")} {
                padding-bottom: 24px;
            }
            ${({ isCheckoutPage }: any) => {
                if (isCheckoutPage) {
                    return css`
                        padding: 16px 0;
                    `;
                }
            }}
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding-top: 32px;
            width: calc(200%/3);
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    linkList1GridItem: {
        css: css`
            padding-left: 0;
            width: 25%;
            ${xScreen("lte", "767")} {
                width: 50%;
                padding-bottom: 32px;
            }
        `,
    },
    linkList2GridItem: {
        css: css`
            padding-left: 0;
            width: 25%;
            ${xScreen("lte", "767")} {
                width: 50%;
                padding-bottom: 32px;
            }
        `,
    },
    linkList3GridItem: {
        css: css`
            padding-left: 0;
            width: 25%;
            ${xScreen("lte", "767")} {
                width: 50%;
            }
        `,
    },
    linkList4GridItem: {
        css: css`
            padding-left: 0;
            width: 25%;
            ${xScreen("lte", "767")} {
                width: 50%;
            }
        `,
    },
    contactUsGridItem: {
        css: css`
            &&& {
                display: flex;
                width: calc(100% / 3);
                ${xScreen("gt", "md")} {
                    margin-top: 32px;
                    padding-left: 33px;
                }
                ${xScreen("lte", "md")} {
                    padding-top: 52px;
                    width: 100%;
                }
            }
        `,
    },
    richContentWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            line-height: 16px;
            &&& {
                padding-top: 52px;
            }
            ${xScreen("lte", "767")} {
                flex-direction: column;
                align-items: flex-start;
                gap: 24px 0;
            }
        `,
    },
    checkoutRichContentWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            width: 100%;
            line-height: 16px;
        `,
    },
    privacyPloicyContainer: {
        css: css`
            ${({ isCheckoutPage }: any) => {
                if (isCheckoutPage) {
                    return css`
                        ${xScreen("lte", "767")} {
                            width: 100%;
                            margin-top: 16px;
                            div[class^="ContentWrapper"] {
                                display: flex;
                                justify-content: center;
                            }
                        }

                    `;
                }
            }}
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: getIsCheckoutPage(state)
    }));

    return (
        <StyledWrapper {...styles.widgetGridContainer} {...{isCheckoutPage}}>
            {
                !isCheckoutPage &&
                <>
                    <StyledWrapper {...styles.linksWrapper}>
                        <StyledWrapper {...styles.linkList1GridItem}>
                            <Zone zoneName="linkList1" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkList2GridItem}>
                            <Zone zoneName="linkList2" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkList3GridItem}>
                            <Zone zoneName="linkList3" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkList4GridItem}>
                            <Zone zoneName="linkList4" contentId={id} />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contactUsGridItem}>
                        <Zone zoneName="ContactUs" contentId={id} />
                    </StyledWrapper>
                </>
            }
            <StyledWrapper {...(isCheckoutPage ? styles.checkoutRichContentWrapper : styles.richContentWrapper)}>
                <StyledWrapper style={{marginRight: "32px"}}>
                    <Typography size={12} lineHeight="16px" weight={400} color="common.white">{(siteMessage("Plumbers_Copy_Right") as string).replace("{0}", new Date().getFullYear().toString())}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.privacyPloicyContainer} {...{isCheckoutPage}}>
                    <Zone zoneName="RichContent2" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
